import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Statistic,
  Select,
  DatePicker,
  Progress,
  Table,
  Tag,
  Space,
  Button,
  message,
} from 'antd';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  TrophyOutlined,
  ClockCircleOutlined,
  StarOutlined,
  DollarOutlined,
  CarOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { deliveryService } from '../services/deliveryService';

const { Title, Paragraph, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const Analytics = () => {
  const [timeRange, setTimeRange] = useState('week');
  const [loading, setLoading] = useState(false); // eslint-disable-line no-unused-vars
  const [analyticsData, setAnalyticsData] = useState({
    earnings: [],
    deliveries: [],
    performance: [],
    ratings: [],
  });

  useEffect(() => {
    const fetchAnalyticsData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('delivery_token');
        if (!token) {
          setLoading(false);
          return;
        }

        // Fetch real data from backend
        const [analyticsRes, earningsRes, statsRes] = await Promise.all([
          deliveryService.getAnalytics({ range: timeRange }),
          deliveryService.getEarnings({ range: timeRange }),
          deliveryService.getDeliveryStats(),
          deliveryService.getReviews(),
        ]);

        const analytics = analyticsRes.data.data || {};
        const earnings = earningsRes.data.data || {};
        const stats = statsRes.data.data || {};

        // Build weekly chart data from monthly_earnings or analytics weekly_data
        let earningsChart = analytics.weekly_data || earnings.weekly_data || [];
        if (earningsChart.length === 0 && earnings.monthly_earnings) {
          earningsChart = earnings.monthly_earnings.map((m) => ({
            date: m.month,
            earnings: parseFloat(m.earnings) || 0,
            deliveries: m.orders || 0,
          }));
        }
        if (earningsChart.length === 0) {
          earningsChart = [
            { date: 'Mon', earnings: 0, deliveries: 0 },
            { date: 'Tue', earnings: 0, deliveries: 0 },
            { date: 'Wed', earnings: 0, deliveries: 0 },
            { date: 'Thu', earnings: 0, deliveries: 0 },
            { date: 'Fri', earnings: 0, deliveries: 0 },
            { date: 'Sat', earnings: 0, deliveries: 0 },
            { date: 'Sun', earnings: 0, deliveries: 0 },
          ];
        }

        setAnalyticsData({
          earnings: earningsChart,
          deliveries: analytics.delivery_hours || [
            { hour: '8AM', count: 0 },
            { hour: '10AM', count: 0 },
            { hour: '12PM', count: 0 },
            { hour: '2PM', count: 0 },
            { hour: '4PM', count: 0 },
            { hour: '6PM', count: 0 },
            { hour: '8PM', count: 0 },
          ],
          performance: [
            { metric: 'On-time Delivery', value: analytics.on_time_percentage || 0, target: 90 },
            {
              metric: 'Customer Satisfaction',
              value: analytics.average_rating || stats.average_rating || 0,
              target: 4.5,
            },
            {
              metric: 'Average Delivery Time',
              value: analytics.average_delivery_time || 0,
              target: 30,
            },
            { metric: 'Daily Deliveries', value: analytics.daily_average || 0, target: 10 },
          ],
          ratings: analytics.rating_distribution || [
            { rating: 5, count: 0, percentage: 0 },
            { rating: 4, count: 0, percentage: 0 },
            { rating: 3, count: 0, percentage: 0 },
            { rating: 2, count: 0, percentage: 0 },
            { rating: 1, count: 0, percentage: 0 },
          ],
          _earnings: earnings,
          _stats: stats,
        });
      } catch (error) {
        console.error('Error fetching analytics data:', error);
        message.error('Failed to load analytics data');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalyticsData();
  }, [timeRange]);

  const COLORS = ['#52c41a', '#1890ff', '#faad14', '#ff4d4f', '#722ed1'];

  const totalEarnings =
    parseFloat(analyticsData._earnings?.total_earnings) ||
    analyticsData.earnings.reduce((sum, day) => sum + day.earnings, 0);
  const totalDeliveries =
    analyticsData._stats?.total_deliveries ||
    analyticsData.earnings.reduce((sum, day) => sum + day.deliveries, 0);
  const totalRatingCount = analyticsData.ratings.reduce((sum, r) => sum + r.count, 0);
  const averageRating =
    totalRatingCount > 0
      ? analyticsData.ratings.reduce((sum, r) => sum + r.rating * r.count, 0) / totalRatingCount
      : analyticsData._stats?.average_rating || 0;
  const averageDeliveryTime =
    analyticsData.performance.find((p) => p.metric === 'Average Delivery Time')?.value || 0;

  const performanceColumns = [
    {
      title: 'Metric',
      dataIndex: 'metric',
      key: 'metric',
    },
    {
      title: 'Current',
      dataIndex: 'value',
      key: 'value',
      render: (value, record) => (
        <div>
          <Text strong>{value}</Text>
          {record.metric === 'Customer Satisfaction' && (
            <div style={{ marginTop: '4px' }}>
              <StarOutlined style={{ color: '#faad14' }} />
              <span style={{ marginLeft: '4px' }}>{value}/5</span>
            </div>
          )}
          {record.metric === 'Average Delivery Time' && (
            <div style={{ marginTop: '4px' }}>
              <Text type="secondary">minutes</Text>
            </div>
          )}
          {record.metric === 'On-time Delivery' && (
            <div style={{ marginTop: '4px' }}>
              <Text type="secondary">%</Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Target',
      dataIndex: 'target',
      key: 'target',
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => {
        const isAboveTarget = record.value >= record.target;
        return (
          <Tag color={isAboveTarget ? 'success' : 'warning'}>
            {isAboveTarget ? 'Above Target' : 'Below Target'}
          </Tag>
        );
      },
    },
  ];

  const exportReport = () => {
    message.success('Analytics report exported successfully!');
  };

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Analytics Dashboard</Title>
      <Paragraph style={{ color: '#666', marginBottom: '32px' }}>
        Track your delivery performance, earnings trends, and customer satisfaction
      </Paragraph>

      {/* Time Range Selector */}
      <Card style={{ marginBottom: '24px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '16px',
          }}
        >
          <Space>
            <Select value={timeRange} onChange={setTimeRange} style={{ width: 120 }}>
              <Option value="today">Today</Option>
              <Option value="week">This Week</Option>
              <Option value="month">This Month</Option>
              <Option value="year">This Year</Option>
            </Select>
            <RangePicker />
          </Space>
          <Button icon={<DownloadOutlined />} onClick={exportReport}>
            Export Report
          </Button>
        </div>
      </Card>

      {/* Key Metrics */}
      <Row gutter={[24, 24]} style={{ marginBottom: '32px' }}>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Total Earnings"
              value={totalEarnings}
              precision={2}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#1890ff' }}
              suffix="$"
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Total Deliveries"
              value={totalDeliveries}
              prefix={<CarOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Average Rating"
              value={averageRating}
              precision={1}
              prefix={<StarOutlined />}
              valueStyle={{ color: '#faad14' }}
              suffix="/5"
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Avg Delivery Time"
              value={averageDeliveryTime}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#722ed1' }}
              suffix="min"
            />
          </Card>
        </Col>
      </Row>

      {/* Charts Row 1 */}
      <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
        <Col xs={24} lg={16}>
          <Card title="Earnings Trend">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={analyticsData.earnings}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip formatter={(value) => [`$${value}`, 'Earnings']} />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="earnings"
                  stroke="#1890ff"
                  fill="#1890ff"
                  fillOpacity={0.3}
                  name="Earnings ($)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title="Customer Ratings">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={analyticsData.ratings}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                  label={({ rating, percentage }) => `${rating}★ (${percentage}%)`}
                >
                  {analyticsData.ratings.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* Charts Row 2 */}
      <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
        <Col xs={24} lg={16}>
          <Card title="Delivery Volume by Hour">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={analyticsData.deliveries}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hour" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" fill="#52c41a" name="Deliveries" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title="Performance Metrics">
            <div style={{ marginBottom: '16px' }}>
              {analyticsData.performance.map((metric, index) => (
                <div key={index} style={{ marginBottom: '16px' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '4px',
                    }}
                  >
                    <Text style={{ fontSize: '0.9rem' }}>{metric.metric}</Text>
                    <Text style={{ fontSize: '0.9rem' }}>{metric.value}%</Text>
                  </div>
                  <Progress
                    percent={metric.value}
                    strokeColor={metric.value >= metric.target ? '#52c41a' : '#faad14'}
                    size="small"
                  />
                </div>
              ))}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Performance Table */}
      <Card title="Performance Summary">
        <Table
          columns={performanceColumns}
          dataSource={analyticsData.performance}
          rowKey="metric"
          pagination={false}
          size="small"
        />
      </Card>

      {/* Insights */}
      <Row gutter={[24, 24]} style={{ marginTop: '24px' }}>
        <Col xs={24} sm={8}>
          <Card title="🏆 Top Achievement">
            <div style={{ textAlign: 'center' }}>
              <TrophyOutlined
                style={{ fontSize: '32px', color: '#faad14', marginBottom: '16px' }}
              />
              <Title level={5}>Speed Demon</Title>
              <Paragraph style={{ fontSize: '0.9rem' }}>
                Completed 15 deliveries with average time of 25 minutes
              </Paragraph>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card title="📈 Peak Performance">
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontSize: '2rem',
                  fontWeight: 'bold',
                  color: '#52c41a',
                  marginBottom: '8px',
                }}
              >
                4PM - 6PM
              </div>
              <Paragraph style={{ fontSize: '0.9rem' }}>
                Your busiest and most productive hours
              </Paragraph>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card title="💡 Improvement Area">
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontSize: '2rem',
                  fontWeight: 'bold',
                  color: '#1890ff',
                  marginBottom: '8px',
                }}
              >
                8AM - 10AM
              </div>
              <Paragraph style={{ fontSize: '0.9rem' }}>
                Consider starting earlier for more opportunities
              </Paragraph>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Weekly Goals */}
      <Card title="Weekly Goals" style={{ marginTop: '24px' }}>
        <Row gutter={[24, 24]}>
          <Col xs={24} sm={8}>
            <div style={{ textAlign: 'center' }}>
              <Progress type="circle" percent={75} size={80} strokeColor="#1890ff" />
              <div style={{ marginTop: '16px' }}>
                <Title level={5}>Earnings Goal</Title>
                <Paragraph>$750 of $1000</Paragraph>
              </div>
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div style={{ textAlign: 'center' }}>
              <Progress type="circle" percent={85} size={80} strokeColor="#52c41a" />
              <div style={{ marginTop: '16px' }}>
                <Title level={5}>Delivery Goal</Title>
                <Paragraph>68 of 80 deliveries</Paragraph>
              </div>
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div style={{ textAlign: 'center' }}>
              <Progress type="circle" percent={92} size={80} strokeColor="#faad14" />
              <div style={{ marginTop: '16px' }}>
                <Title level={5}>Rating Goal</Title>
                <Paragraph>4.6 of 4.5 stars</Paragraph>
              </div>
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default Analytics;
